import type { CSSProperties } from 'react';

/**
 * Skeleton primitives — Citadel Light Authority shimmer placeholders.
 *
 * Replaces the screen-wide `.loading-bar` italic strip with structured
 * placeholders that match the eventual content layout. Use these while
 * a table, card or single line is fetching so the UI doesn't reflow
 * when real data arrives.
 *
 * Shimmer is a 1.2s linear-gradient sweep over `--surface-alt`. The CSS
 * lives in Screen.css and honours `prefers-reduced-motion`.
 */

interface LineSkeletonProps {
  /** CSS width — '100%', '60%', '8em', 240, etc. Default 100%. */
  width?: string | number;
  /** CSS height — defaults to '1em'. */
  height?: string | number;
  /** Optional class hook for spacing. */
  className?: string;
  style?: CSSProperties;
}

export function LineSkeleton({
  width = '100%',
  height = '1em',
  className,
  style,
}: LineSkeletonProps) {
  const merged: CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    ...style,
  };
  return (
    <span
      className={['skeleton', 'skeleton-line', className].filter(Boolean).join(' ')}
      style={merged}
      aria-hidden="true"
    />
  );
}

interface RowSkeletonProps {
  /** Number of cells in the row. */
  columns: number;
}

/**
 * A single tr-styled placeholder row, intended to be rendered inside a
 * `<tbody>` of a `.data-table`. Each cell hosts one LineSkeleton with
 * a pseudo-random width drawn from a stable pattern so the page doesn't
 * jitter between renders.
 */
export function RowSkeleton({ columns }: RowSkeletonProps) {
  const widths = ['60%', '80%', '40%', '90%'];
  return (
    <tr className="skeleton-row" aria-hidden="true">
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i}>
          <LineSkeleton width={widths[i % widths.length]} />
        </td>
      ))}
    </tr>
  );
}

interface TableSkeletonProps {
  rows: number;
  columns: number;
  /** Optional headers; if omitted, a generic shimmer header row renders. */
  headers?: ReadonlyArray<string>;
}

/**
 * A full table placeholder — thead + N body rows. Drop in where the real
 * `<table className="data-table">` would render while data is loading.
 */
export function TableSkeleton({ rows, columns, headers }: TableSkeletonProps) {
  return (
    <table className="data-table skeleton-table" aria-hidden="true">
      <thead>
        <tr>
          {Array.from({ length: columns }).map((_, i) => (
            <th key={i}>
              {headers && headers[i] ? (
                headers[i]
              ) : (
                <LineSkeleton width="6em" height="0.8em" />
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, i) => (
          <RowSkeleton key={i} columns={columns} />
        ))}
      </tbody>
    </table>
  );
}

/**
 * A card-shaped placeholder — title bar + 3 line bars. Drop in for
 * surface-card-style detail panels (e.g. finding detail panel,
 * engagement summary card).
 */
export function CardSkeleton() {
  return (
    <div className="skeleton-card" aria-hidden="true">
      <LineSkeleton width="40%" height="1.5em" className="skeleton-card-title" />
      <LineSkeleton width="100%" />
      <LineSkeleton width="92%" />
      <LineSkeleton width="60%" />
    </div>
  );
}
